import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import Logo from '../components/Logo';
import HamburgerMenuButton from '../components/HamburgerMenuButton';
import InboundSyncImportBanner from '../components/InboundSyncImportBanner';
import useDismissOnOutsideClick from '../hooks/useDismissOnOutsideClick';
import { DeveloperMenuItem } from '../components/DeveloperNavigation';
import './Settings.css';
import './ContractsList.css';
import { getContractApiUrl, getTmsApiUrl } from '../utils/apiUrls';
import { optionalAuthHeaders } from '../utils/billingApi';

const BACKEND_URL = getContractApiUrl();
const TMS_URL = getTmsApiUrl();

const DEFAULT_TEAM_TC_UI_ROLE = 'Transaction Coordinator';

const normalizeDefaultTeamRole = (role) => {
    if (!role) return DEFAULT_TEAM_TC_UI_ROLE;
    const normalized = role.toLowerCase();
    if (
        normalized.includes('transaction coordinator')
        || normalized === 'tc'
    ) {
        return DEFAULT_TEAM_TC_UI_ROLE;
    }
    return role;
};

const Settings = () => {
    const {
        user,
        isAuthenticated,
        isLoading: authLoading,
        logout,
        getAccessTokenSilently,
        getIdTokenClaims,
    } = useAuth0();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('team');
    const [isLoading, setIsLoading] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const userMenuRef = useRef(null);
    const dismissUserMenu = useCallback(() => {
        setShowDropdown(false);
    }, []);
    useDismissOnOutsideClick(userMenuRef, showDropdown, dismissUserMenu);

    // User profile state (needed for ID and subscriber status)
    const [userProfile, setUserProfile] = useState(null);

    // Email Behavior Mode state
    const [emailBehaviorMode, setEmailBehaviorMode] = useState('forwarding');
    const [savingMode, setSavingMode] = useState(false);
    const [modeMessage, setModeMessage] = useState(null);

    // My Team state
    const [defaultTeam, setDefaultTeam] = useState([]);
    const [newMember, setNewMember] = useState({ name: '', email: '', role: 'Transaction Coordinator' });
    const [autocompleteMembers, setAutocompleteMembers] = useState([]);
    const [showAutocomplete, setShowAutocomplete] = useState(false);

    // TMS / Integrations state
    const [tmsConfig, setTmsConfig] = useState(null);        // null = not connected
    const [tmsForm, setTmsForm] = useState({ access_key: '', access_secret: '' });
    const [savingTms, setSavingTms] = useState(false);
    const [tmsMessage, setTmsMessage] = useState(null);
    const [tmsShowField, setTmsShowField] = useState({ access_key: false, access_secret: false });
    const [inboundSyncStatus, setInboundSyncStatus] = useState(null);
    const [inboundKickoff, setInboundKickoff] = useState(null);
    const [retryingInboundSync, setRetryingInboundSync] = useState(false);

    const SkyslopeKeyHelp = () => (
        <div className="tms-form-hint">
            <strong>How to Find these keys:</strong>
            <br />
            In SkySlope, open <strong>My Account → INTEGRATIONS</strong> → look for the SkySlope API
            section and click &quot;Generate New Key&quot; to create your Access Key and Access Secret.
            Paste the keys here.
        </div>
    );

    // Branding state
    const [brandingType, setBrandingType] = useState('header');
    const [brandingConfig, setBrandingConfig] = useState(null);
    const [headerFile, setHeaderFile] = useState(null);
    const [headerPreview, setHeaderPreview] = useState(null);
    const [headshotFile, setHeadshotFile] = useState(null);
    const [headshotPreview, setHeadshotPreview] = useState(null);
    const [titleFile, setTitleFile] = useState(null);
    const [titlePreview, setTitlePreview] = useState(null);
    const [savingBranding, setSavingBranding] = useState(false);
    const [brandingMessage, setBrandingMessage] = useState(null);
    const [emailPreviewHtml, setEmailPreviewHtml] = useState('');

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/');
            return;
        }
        if (isAuthenticated && user?.email) {
            fetchUserData();
        }
    }, [isAuthenticated, user, authLoading]);

    useEffect(() => {
        const tab = (searchParams.get('tab') || '').toLowerCase();
        const allowed = new Set(['team', 'email', 'branding', 'integrations']);
        if (allowed.has(tab)) {
            setActiveTab(tab);
        }
    }, [searchParams]);

    useEffect(() => {
        if (userProfile?.id) {
            fetchDefaultTeam();
            fetchAutocompleteMembers();
            fetchBranding();
            fetchTmsConfig();
        }
    }, [userProfile?.id]);

    const fetchInboundSyncStatus = useCallback(async () => {
        if (!user?.email || !tmsConfig?.tms_type) {
            return null;
        }
        try {
            const res = await axios.get(`${TMS_URL}/inbound-sync/status?email=${encodeURIComponent(user.email)}`);
            setInboundSyncStatus(res.data);
            return res.data;
        } catch (err) {
            if (err.response?.status !== 404) {
                console.error('Error fetching inbound sync status:', err);
            }
            return null;
        }
    }, [user?.email, tmsConfig?.tms_type]);

    useEffect(() => {
        if (tmsConfig?.tms_type === 'skyslope') {
            fetchInboundSyncStatus();
        }
    }, [tmsConfig?.tms_type, fetchInboundSyncStatus]);

    const normalizedInboundSyncStatus = (inboundSyncStatus?.status || '').toLowerCase();
    const inboundSyncInProgress = ['running', 'pending'].includes(normalizedInboundSyncStatus);
    const inboundSyncCompleted = normalizedInboundSyncStatus === 'completed'
        || normalizedInboundSyncStatus === 'complete';
    const inboundSyncTerminal = inboundSyncCompleted || normalizedInboundSyncStatus === 'failed';
    const kickoffJobId = inboundKickoff?.job_id ?? null;
    const statusJobId = inboundSyncStatus?.job_id ?? null;
    const kickoffAwaitingStatus = Boolean(kickoffJobId && statusJobId !== kickoffJobId);
    const kickoffResolved = Boolean(
        kickoffJobId
        && statusJobId === kickoffJobId
        && inboundSyncTerminal,
    );
    const kickoffStillActive = Boolean(
        inboundKickoff?.started || inboundKickoff?.already_running,
    ) && !kickoffResolved;
    const shouldPollInboundSync = tmsConfig?.tms_type === 'skyslope' && (
        inboundSyncInProgress
        || kickoffAwaitingStatus
        || kickoffStillActive
    );

    useEffect(() => {
        if (!shouldPollInboundSync) {
            return undefined;
        }
        const timer = setInterval(() => {
            fetchInboundSyncStatus();
        }, 5000);
        return () => clearInterval(timer);
    }, [shouldPollInboundSync, fetchInboundSyncStatus]);

    useEffect(() => {
        if (!inboundKickoff?.job_id || !inboundSyncStatus?.job_id) {
            return;
        }
        if (inboundSyncStatus.job_id !== inboundKickoff.job_id) {
            return;
        }
        const status = (inboundSyncStatus.status || '').toLowerCase();
        if (['completed', 'complete', 'failed'].includes(status)) {
            setInboundKickoff(null);
        }
    }, [inboundSyncStatus?.status, inboundSyncStatus?.job_id, inboundKickoff?.job_id]);

    const fetchUserData = async () => {
        try {
            const [profileRes, settingsRes] = await Promise.all([
                axios.get(`${BACKEND_URL}/user/profile?email=${user.email}`),
                axios.get(`${BACKEND_URL}/user/settings?email=${user.email}`)
            ]);
            setUserProfile(profileRes.data);
            setEmailBehaviorMode(settingsRes.data.email_behavior_mode || 'forwarding');
        } catch (err) {
            console.error('Error fetching user data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDefaultTeam = async () => {
        if (!userProfile?.id) return;
        try {
            const response = await axios.get(`${BACKEND_URL}/users/${userProfile.id}/default-team`);
            setDefaultTeam(response.data.members || []);
        } catch (err) {
            console.error('Error fetching default team:', err);
        }
    };

    const fetchAutocompleteMembers = async () => {
        if (!userProfile?.id) return;
        try {
            const response = await axios.get(`${BACKEND_URL}/users/${userProfile.id}/previous-team`);
            setAutocompleteMembers(response.data.members || []);
        } catch (err) {
            console.error('Error fetching autocomplete:', err);
        }
    };

    const handleAddDefaultMember = async () => {
        if (!newMember.email || !userProfile?.id) return;
        try {
            await axios.post(`${BACKEND_URL}/users/${userProfile.id}/default-team`, {
                agent_email: user.email,
                member_email: newMember.email,
                member_name: newMember.name,
                role: newMember.role
            });
            fetchDefaultTeam();
            setNewMember({ name: '', email: '', role: 'Transaction Coordinator' });
        } catch (err) {
            console.error('Error adding team member:', err);
        }
    };

    const handleRemoveDefaultMember = async (memberId) => {
        if (!userProfile?.id) return;
        try {
            await axios.delete(`${BACKEND_URL}/users/${userProfile.id}/default-team/${memberId}`);
            fetchDefaultTeam();
        } catch (err) {
            console.error('Error removing team member:', err);
        }
    };

    const handleModeChange = async (newMode) => {
        if (newMode === emailBehaviorMode) return;
        setSavingMode(true);
        setModeMessage(null);

        try {
            await axios.put(`${BACKEND_URL}/user/settings`, {
                email: user.email,
                email_behavior_mode: newMode
            });
            setEmailBehaviorMode(newMode);
            setModeMessage('Email behavior updated successfully.');
            setTimeout(() => setModeMessage(null), 3000);
        } catch (err) {
            console.error('Error updating email behavior mode:', err);
            setModeMessage('Error updating settings. Please try again.');
        } finally {
            setSavingMode(false);
        }
    };

    // --- Branding handlers ---
    const brandingAuthHeaders = async () => (
        optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims })
    );

    const fetchBranding = async () => {
        try {
            const headers = await brandingAuthHeaders();
            const res = await axios.get(
                `${BACKEND_URL}/user/branding?email=${encodeURIComponent(user.email)}`,
                { headers },
            );
            const config = res.data.branding_config;
            setBrandingConfig(config);
            if (config) {
                setBrandingType(config.type || 'header');
                if (config.header_url) setHeaderPreview(config.header_url);
                if (config.headshot_url) setHeadshotPreview(config.headshot_url);
                if (config.title_url) setTitlePreview(config.title_url);
            }
            updateEmailPreview(config);
        } catch (err) {
            console.error('Error fetching branding:', err);
        }
    };

    const updateEmailPreview = (config) => {
        // Build a simplified preview of the critical dates email header + footer
        let headerHtml = '';
        if (config) {
            if (config.type === 'header' && config.header_url) {
                headerHtml = `<div style="text-align: center; margin-bottom: 30px;"><img src="${config.header_url}" alt="Branding" style="max-width: 300px; max-height: 75px; width: auto; height: auto; border: none;"></div>`;
            } else if (config.type === 'headshot_title') {
                let parts = '';
                if (config.headshot_url) parts += `<img src="${config.headshot_url}" alt="Photo" style="width: 75px; height: 75px; border-radius: 50%; object-fit: cover; border: none; vertical-align: middle;">`;
                if (config.title_url) {
                    if (config.headshot_url) parts += '<span style="width: 12px; display: inline-block;"></span>';
                    parts += `<img src="${config.title_url}" alt="Title" style="max-width: 200px; max-height: 75px; width: auto; height: auto; border: none; vertical-align: middle;">`;
                }
                headerHtml = `<div style="text-align: center; margin-bottom: 30px;">${parts}</div>`;
            }
        }

        const previewHtml = `
            <html><body style="margin: 0; padding: 0; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; background: #f9f9f9;">
            <div style="max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 16px; padding: 40px 30px; box-shadow: 0 4px 24px rgba(0,0,0,0.08);">
                ${headerHtml}
                <div style="text-align: center;">
                    <h1 style="font-size: 28px; font-weight: 700; color: #1F2937; margin: 0 0 16px;">Congratulations!</h1>
                    <p style="font-size: 16px; color: #4B5563; line-height: 1.6; margin: 0 0 30px;">You are under contract to buy <strong>123 Main Street</strong>. Here are the key milestones.</p>
                </div>
                <div style="background: #ffffff; border-radius: 12px; border: 1px solid #E5E7EB; overflow: hidden; margin-bottom: 30px;">
                    <div style="background: #1F2937; color: #ffffff; padding: 14px 20px; font-size: 16px; font-weight: 600;">Key Milestones to Close</div>
                    <div style="padding: 8px 20px;">
                        <div style="display: flex; padding: 12px 0; border-bottom: 1px solid #F3F4F6;"><span style="min-width: 90px; color: #5F8D4E; font-weight: 600;">Feb 15</span><span style="color: #374151;">Purchase Agreement Signed</span></div>
                        <div style="display: flex; padding: 12px 0; border-bottom: 1px solid #F3F4F6;"><span style="min-width: 90px; color: #5F8D4E; font-weight: 600;">Feb 18</span><span style="color: #374151;">Earnest Money Due</span></div>
                        <div style="display: flex; padding: 12px 0; border-bottom: 1px solid #F3F4F6;"><span style="min-width: 90px; color: #5F8D4E; font-weight: 600;">Feb 25</span><span style="color: #374151;">Inspection Deadline</span></div>
                        <div style="display: flex; padding: 12px 0; background: #F0FFF4; border-radius: 6px; padding-left: 8px;"><span style="min-width: 90px; color: #5F8D4E; font-weight: 600;">Mar 15</span><span style="color: #374151; font-weight: 600;">Close of Escrow</span></div>
                    </div>
                </div>
                <div style="text-align: center; font-size: 14px; color: #6B7280; line-height: 1.5; margin-top: 40px;">
                    Do not hesitate to reach out to me at <a href="#" style="color: inherit; text-decoration: underline;">tc@keyflo.ai</a><br>
                    or your agent with any questions about timelines or next steps.<br><br>
                    <img src="https://storage.googleapis.com/keyflo-public-assets/keyflo_logo.png" alt="KeyFlo" style="width: 100px; height: auto; border: none;">
                </div>
            </div>
            </body></html>
        `;
        setEmailPreviewHtml(previewHtml);
    };

    const handleFileSelect = (file, type) => {
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const url = e.target.result;
            if (type === 'header') { setHeaderFile(file); setHeaderPreview(url); }
            else if (type === 'headshot') { setHeadshotFile(file); setHeadshotPreview(url); }
            else if (type === 'title') { setTitleFile(file); setTitlePreview(url); }
        };
        reader.readAsDataURL(file);
    };

    const handleDrop = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer?.files?.[0];
        handleFileSelect(file, type);
    };

    const handleSaveBranding = async () => {
        setSavingBranding(true);
        setBrandingMessage(null);
        try {
            const formData = new FormData();
            formData.append('email', user.email);
            formData.append('branding_type', brandingType);

            if (brandingType === 'header') {
                if (!headerFile && !brandingConfig?.header_url) {
                    setBrandingMessage('Please upload a header image first.');
                    setSavingBranding(false);
                    return;
                }
                if (headerFile) formData.append('header_image', headerFile);
            } else {
                if (!headshotFile && !titleFile && !brandingConfig?.headshot_url && !brandingConfig?.title_url) {
                    setBrandingMessage('Please upload at least one image.');
                    setSavingBranding(false);
                    return;
                }
                if (headshotFile) formData.append('headshot_image', headshotFile);
                if (titleFile) formData.append('title_image', titleFile);
            }

            const authHeaders = await brandingAuthHeaders();
            const res = await axios.post(`${BACKEND_URL}/user/branding`, formData, {
                headers: {
                    ...authHeaders,
                    'Content-Type': 'multipart/form-data',
                },
            });
            setBrandingConfig(res.data.branding_config);
            updateEmailPreview(res.data.branding_config);
            setBrandingMessage('Branding saved successfully!');
            // Clear file objects (previews remain from URLs)
            setHeaderFile(null);
            setHeadshotFile(null);
            setTitleFile(null);
            setTimeout(() => setBrandingMessage(null), 3000);
        } catch (err) {
            console.error('Error saving branding:', err);
            const status = err.response?.status;
            const detail = err.response?.data?.error || err.message;
            const contentType = String(err.response?.headers?.['content-type'] || '');
            const flaskAlreadyAlerted = contentType.includes('application/json');
            if ((status === 413 || (status && status >= 500)) && !flaskAlreadyAlerted) {
                try {
                    const headers = await brandingAuthHeaders();
                    await axios.post(
                        `${BACKEND_URL}/user/report-api-error`,
                        {
                            email: user.email,
                            path: '/contract/user/branding',
                            method: 'POST',
                            status_code: status,
                            error: detail,
                        },
                        { headers },
                    );
                } catch (reportErr) {
                    console.warn('Could not report branding API error:', reportErr);
                }
            }
            setBrandingMessage(
                status === 413
                    ? 'Error saving branding: image too large (HTTP 413). Try a smaller file.'
                    : detail
                        ? `Error saving branding: ${detail}`
                        : 'Error saving branding. Please try again.',
            );
        } finally {
            setSavingBranding(false);
        }
    };

    const handleRemoveBranding = async () => {
        if (!window.confirm('Remove all branding images? Emails will have no header image.')) return;
        try {
            const headers = await brandingAuthHeaders();
            await axios.delete(
                `${BACKEND_URL}/user/branding?email=${encodeURIComponent(user.email)}`,
                { headers },
            );
            setBrandingConfig(null);
            setHeaderFile(null); setHeaderPreview(null);
            setHeadshotFile(null); setHeadshotPreview(null);
            setTitleFile(null); setTitlePreview(null);
            updateEmailPreview(null);
            setBrandingMessage('Branding removed.');
            setTimeout(() => setBrandingMessage(null), 3000);
        } catch (err) {
            console.error('Error removing branding:', err);
            const detail = err.response?.data?.error || err.message;
            setBrandingMessage(
                detail
                    ? `Error removing branding: ${detail}`
                    : 'Error removing branding. Please try again.',
            );
        }
    };

    // ── TMS / Integrations handlers ──────────────────────────────────────────

    const fetchTmsConfig = async ({ raiseOnError = false } = {}) => {
        try {
            const res = await axios.get(`${TMS_URL}/config?email=${user.email}`);
            if (res.data.tms_type) {
                setTmsConfig(res.data);
                setTmsForm(f => ({
                    ...f,
                    access_key: res.data.access_key || f.access_key,
                    access_secret: res.data.access_secret || '',
                }));
            } else {
                setTmsConfig(null);
                setTmsForm({ access_key: '', access_secret: '' });
            }
            return true;
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error fetching TMS config:', err);
            if (raiseOnError) throw err;
            return false;
        }
    };

    const handleConnectTms = async () => {
        setSavingTms(true);
        setTmsMessage(null);
        try {
            const res = await axios.post(`${TMS_URL}/config`, {
                email: user.email,
                tms_type: 'skyslope',
                ...tmsForm,
            });
            if (res.data.success) {
                setInboundSyncStatus(null);
                setInboundKickoff(res.data.inbound_sync || null);
                try {
                    await fetchTmsConfig({ raiseOnError: true });
                    if (res.data.inbound_sync?.error) {
                        setTmsMessage({
                            type: 'error',
                            text: `Connected to SkySlope — ${res.data.office_name || 'office found'}, but import could not start. Use Retry import below.`,
                        });
                    } else {
                        setTmsMessage({
                            type: 'success',
                            text: `Connected to SkySlope — ${res.data.office_name || 'office found'}.`,
                        });
                    }
                    await fetchInboundSyncStatus();
                } catch {
                    setTmsMessage({
                        type: 'error',
                        text: 'SkySlope connected, but settings could not be refreshed. Reload the page to confirm.',
                    });
                }
            } else {
                setTmsMessage({ type: 'error', text: res.data.error || 'Connection failed. Check your credentials.' });
            }
        } catch (err) {
            setTmsMessage({ type: 'error', text: err.response?.data?.error || 'Connection failed. Check your credentials.' });
        } finally {
            setSavingTms(false);
            setTimeout(() => setTmsMessage(null), 5000);
        }
    };

    const handleDisconnectTms = async () => {
        if (!window.confirm('Disconnect SkySlope? New contracts will no longer be synced automatically.')) return;
        try {
            await axios.delete(`${TMS_URL}/config`, { data: { email: user.email } });
            setTmsConfig(null);
            setTmsForm({ access_key: '', access_secret: '' });
            setInboundKickoff(null);
            setInboundSyncStatus(null);
            setTmsMessage({ type: 'success', text: 'SkySlope disconnected.' });
            setTimeout(() => setTmsMessage(null), 3000);
        } catch (err) {
            console.error('Error disconnecting TMS:', err);
        }
    };

    const inboundSyncStatusLabel = () => {
        if (!inboundSyncStatus || inboundSyncStatus.status === 'never_run') {
            return 'No import has run yet.';
        }
        const parts = [`Status: ${inboundSyncStatus.status}`];
        if (inboundSyncStatus.contracts_created != null) {
            parts.push(`${inboundSyncStatus.contracts_created} created`);
        }
        if (inboundSyncStatus.contracts_updated != null) {
            parts.push(`${inboundSyncStatus.contracts_updated} updated`);
        }
        if (inboundSyncStatus.listings_created != null) {
            parts.push(`${inboundSyncStatus.listings_created} listings created`);
        }
        if (inboundSyncStatus.error_message) {
            parts.push(inboundSyncStatus.error_message);
        }
        if ((inboundSyncStatus.status || '').toLowerCase() === 'running') {
            parts.push('counts update when the sync finishes');
        }
        return parts.join(' · ');
    };

    const showImportStartedMessage = inboundSyncInProgress;
    const showImportRetry = !inboundSyncInProgress
        && !inboundSyncCompleted
        && (
            Boolean(inboundKickoff?.error)
            || normalizedInboundSyncStatus === 'never_run'
            || normalizedInboundSyncStatus === 'failed'
        );

    const handleRetryInboundSync = async () => {
        setRetryingInboundSync(true);
        setTmsMessage(null);
        try {
            const res = await axios.post(`${TMS_URL}/inbound-sync/start`, { email: user.email });
            const jobId = res.data?.job_id;
            setInboundSyncStatus(null);
            setInboundKickoff({
                started: true,
                job_id: jobId,
            });
            setTmsMessage({ type: 'success', text: 'SkySlope import started.' });
            await fetchInboundSyncStatus();
        } catch (err) {
            const detail = err.response?.data?.error || err.message || 'Could not start import';
            setInboundKickoff({ started: false, error: detail });
            setTmsMessage({ type: 'error', text: `Import could not start: ${detail}` });
        } finally {
            setRetryingInboundSync(false);
        }
    };

    const handleLogout = () => { sessionStorage.removeItem('keyflo_login_event_fired'); logout({ logoutParams: { returnTo: window.location.origin } }); };
    const handleDropdownToggle = () => setShowDropdown(!showDropdown);

    if (authLoading || (isAuthenticated && isLoading)) return <LoadingSpinner />;

    const MODE_OPTIONS = [
        {
            value: 'forwarding',
            label: 'Forwarding',
            description: 'All emails go to you only, formatted so you can forward them quickly to whomever you choose.',
            icon: '📨',
            enabled: true
        },
        {
            value: 'ask_first',
            label: 'Ask First',
            description: 'KeyFlo decides who to send emails to, but always asks your permission before sending to others.',
            icon: '✋',
            enabled: true
        },
        {
            value: 'autonomous',
            label: 'Autonomous',
            description: 'KeyFlo automatically sends emails directly to everyone on the transaction team to maximize efficiency and coordination.',
            note: 'Autonomous mode also allows other users to ask questions about the transaction to KeyFlo without needing to involve you in the conversation.',
            icon: '🚀',
            enabled: true
        }
    ];

    return (
        <div className="settings-page-container">
            <header className="app-header">
                <div className="header-content">
                    <div className="header-left">
                        <Logo size={40} variant="arrow" />
                        <span className="settings-page-title">Settings</span>
                    </div>
                    <div className="user-nav">
                        <button className="back-button-nav" onClick={() => navigate('/')}>← Back</button>
                        <div className="user-menu" ref={userMenuRef}>
                            <HamburgerMenuButton onClick={handleDropdownToggle} />
                            {showDropdown && (
                                <div className="dropdown-menu">
                                    <button className="menu-item" onClick={() => { setShowDropdown(false); navigate('/profile'); }}>Profile</button>
                                    <button className="menu-item" onClick={() => { setShowDropdown(false); navigate('/settings'); }}>Settings</button>
                                    {user?.email?.endsWith('@keyflo.ai') && (
                                        <DeveloperMenuItem
                                            userEmail={user?.email}
                                            onNavigate={(path) => {
                                                setShowDropdown(false);
                                                navigate(path);
                                            }}
                                        />
                                    )}
                                    <div className="divider"></div>
                                    <button className="menu-item" style={{ color: '#e74c3c' }} onClick={() => { setShowDropdown(false); handleLogout(); }}>Log Out</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="settings-main">
                {/* Tab Navigation */}
                <div className="settings-tabs">
                    <button
                        className={`settings-tab ${activeTab === 'team' ? 'active' : ''}`}
                        onClick={() => setActiveTab('team')}
                    >
                        My Team
                    </button>
                    <button
                        className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
                        onClick={() => setActiveTab('email')}
                    >
                        Email Preferences
                    </button>
                    <button
                        className={`settings-tab ${activeTab === 'branding' ? 'active' : ''}`}
                        onClick={() => setActiveTab('branding')}
                    >
                        Custom Branding
                    </button>
                    <button
                        className={`settings-tab ${activeTab === 'integrations' ? 'active' : ''}`}
                        onClick={() => setActiveTab('integrations')}
                    >
                        Integrations
                    </button>
                </div>

                {/* Tab Content */}
                <div className="settings-content">
                    {/* MY TEAM TAB */}
                    {activeTab === 'team' && (
                        <div className="settings-card">
                            <div className="settings-card-header">
                                <h2>My Team</h2>
                            </div>
                            <div className="settings-card-body">
                                <p className="section-description">
                                    Members you add here will be automatically invited to your new transactions.
                                </p>
                                <div className="my-team-container">
                                    <div className="my-team-list">
                                        {defaultTeam.length === 0 ? (
                                            <p className="no-members">No team members added yet.</p>
                                        ) : (
                                            defaultTeam.map(member => (
                                                <div key={member.id} className="team-member-chip">
                                                    <div className="member-details">
                                                        <strong>{member.member_name || member.member_email}</strong>
                                                        <span>{member.role}</span>
                                                    </div>
                                                    <button onClick={() => handleRemoveDefaultMember(member.id)}>×</button>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="add-team-member">
                                        <h4>Add Team Member</h4>
                                        <div className="add-member-form-inline">
                                            <div className="input-group autocomplete-pkg">
                                                <input
                                                    type="text"
                                                    placeholder="Name or Email"
                                                    value={newMember.email || newMember.name}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setNewMember({ ...newMember, name: val, email: val });
                                                        setShowAutocomplete(val.length > 1);
                                                    }}
                                                    onFocus={() => setShowAutocomplete(newMember.email?.length > 1)}
                                                />
                                                {showAutocomplete && (
                                                    <div className="dropdown-autocomplete">
                                                        {(autocompleteMembers || [])
                                                            .filter(m => {
                                                                const search = (newMember.email || newMember.name || '').toLowerCase();
                                                                return m.name?.toLowerCase().includes(search) ||
                                                                    m.email?.toLowerCase().includes(search);
                                                            })
                                                            .slice(0, 5)
                                                            .map((m, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className="suggestion-item"
                                                                    onClick={() => {
                                                                        setNewMember({
                                                                            name: m.name || '',
                                                                            email: m.email || '',
                                                                            role: normalizeDefaultTeamRole(m.role),
                                                                        });
                                                                        setShowAutocomplete(false);
                                                                    }}
                                                                >
                                                                    {m.name || 'Unknown'} ({m.email || 'No Email'})
                                                                </div>
                                                            ))
                                                        }
                                                        {(autocompleteMembers || [])
                                                            .filter(m => {
                                                                const search = (newMember.email || newMember.name || '').toLowerCase();
                                                                return m.name?.toLowerCase().includes(search) ||
                                                                    m.email?.toLowerCase().includes(search);
                                                            }).length === 0 && (
                                                                <div className="suggestion-item no-matches">No matches found</div>
                                                            )
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                            <select
                                                value={newMember.role}
                                                onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                                            >
                                                <option value="Transaction Coordinator">Transaction Coordinator</option>
                                                <option value="Title Officer">Title Officer</option>
                                                <option value="Escrow Officer">Escrow Officer</option>
                                                <option value="Mortgage Broker">Mortgage Broker</option>
                                                <option value="Lender">Lender</option>
                                                <option value="Insurance Agent">Insurance Agent</option>
                                                <option value="Real Estate Attorney">Attorney</option>
                                            </select>
                                            <button className="add-btn-primary" onClick={handleAddDefaultMember} disabled={!newMember.email}>Add</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* EMAIL PREFERENCES TAB */}
                    {activeTab === 'email' && (
                        <div className="settings-card">
                            <div className="settings-card-header">
                                <h2>Email Behavior Mode</h2>
                            </div>
                            <div className="settings-card-body">
                                <p className="section-description">
                                    Choose how KeyFlo distributes emails to other members of your transaction team.
                                </p>

                                {modeMessage && (
                                    <div className={`settings-message ${modeMessage.includes('Error') ? 'error' : 'success'}`}>
                                        {modeMessage}
                                    </div>
                                )}

                                <div className="mode-options">
                                    {MODE_OPTIONS.map(option => (
                                        <div
                                            key={option.value}
                                            className={`mode-option ${emailBehaviorMode === option.value ? 'selected' : ''} ${!option.enabled ? 'disabled' : ''}`}
                                            onClick={() => option.enabled && handleModeChange(option.value)}
                                        >
                                            <div className="mode-option-header">
                                                <div className="mode-radio">
                                                    <input
                                                        type="radio"
                                                        name="emailBehaviorMode"
                                                        value={option.value}
                                                        checked={emailBehaviorMode === option.value}
                                                        onChange={() => option.enabled && handleModeChange(option.value)}
                                                        disabled={!option.enabled || savingMode}
                                                    />
                                                </div>
                                                <span className="mode-icon">{option.icon}</span>
                                                <span className="mode-label">{option.label}</span>
                                                {option.comingSoon && (
                                                    <span className="coming-soon-badge">Coming Soon</span>
                                                )}
                                            </div>
                                            <p className="mode-description">{option.description}</p>
                                            {option.note && (
                                                <p className="mode-note">
                                                    <em>Note: {option.note}</em>
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {savingMode && (
                                    <div className="saving-indicator">Saving...</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* CUSTOM BRANDING TAB */}
                    {activeTab === 'branding' && (
                        <div className="settings-card">
                            <div className="settings-card-header">
                                <h2>Email Branding</h2>
                            </div>
                            <div className="settings-card-body">
                                <p className="section-description">
                                    Customize how your emails appear to clients and transaction teams. Upload your branding to replace the default KeyFlo header.
                                </p>

                                {brandingMessage && (
                                    <div className={`settings-message ${brandingMessage.includes('Error') ? 'error' : 'success'}`}>
                                        {brandingMessage}
                                    </div>
                                )}

                                {/* Branding Type Selector */}
                                <div className="branding-type-selector">
                                    <div
                                        className={`branding-type-card ${brandingType === 'header' ? 'selected' : ''}`}
                                        onClick={() => setBrandingType('header')}
                                    >
                                        <div className="branding-type-header">
                                            <input
                                                type="radio"
                                                name="brandingType"
                                                value="header"
                                                checked={brandingType === 'header'}
                                                onChange={() => setBrandingType('header')}
                                            />
                                            <strong>Single Header Image</strong>
                                        </div>
                                        <p className="branding-type-desc">Upload one header image</p>
                                    </div>
                                    <div
                                        className={`branding-type-card ${brandingType === 'headshot_title' ? 'selected' : ''}`}
                                        onClick={() => setBrandingType('headshot_title')}
                                    >
                                        <div className="branding-type-header">
                                            <input
                                                type="radio"
                                                name="brandingType"
                                                value="headshot_title"
                                                checked={brandingType === 'headshot_title'}
                                                onChange={() => setBrandingType('headshot_title')}
                                            />
                                            <strong>Headshot + Title Block</strong>
                                        </div>
                                        <p className="branding-type-desc">Upload a headshot and title block</p>
                                    </div>
                                </div>

                                {/* Upload Zones */}
                                <div className="branding-upload-section">
                                    {brandingType === 'header' ? (
                                        <div className="upload-zone-container">
                                            <label className="upload-label">Header Image</label>
                                            <div
                                                className="upload-dropzone"
                                                onDrop={(e) => handleDrop(e, 'header')}
                                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                onClick={() => document.getElementById('header-file-input').click()}
                                            >
                                                {headerPreview ? (
                                                    <div className="upload-preview">
                                                        <img src={headerPreview} alt="Header preview" style={{ maxWidth: '300px', maxHeight: '75px' }} />
                                                    </div>
                                                ) : (
                                                    <div className="upload-placeholder">
                                                        <span className="upload-icon">📁</span>
                                                        <span>Drag & drop or click to upload</span>
                                                        <span className="upload-hint">PNG, JPG, or SVG • Will be scaled to 300×75px</span>
                                                    </div>
                                                )}
                                                <input
                                                    id="header-file-input"
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleFileSelect(e.target.files[0], 'header')}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="upload-zone-row">
                                            <div className="upload-zone-container">
                                                <label className="upload-label">Headshot</label>
                                                <div
                                                    className="upload-dropzone upload-dropzone-small"
                                                    onDrop={(e) => handleDrop(e, 'headshot')}
                                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                    onClick={() => document.getElementById('headshot-file-input').click()}
                                                >
                                                    {headshotPreview ? (
                                                        <div className="upload-preview">
                                                            <img src={headshotPreview} alt="Headshot preview" className="headshot-preview-img" />
                                                        </div>
                                                    ) : (
                                                        <div className="upload-placeholder">
                                                            <span className="upload-icon">👤</span>
                                                            <span>Upload headshot</span>
                                                            <span className="upload-hint">Will be scaled to 75×75px circle </span>
                                                        </div>
                                                    )}
                                                    <input
                                                        id="headshot-file-input"
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => handleFileSelect(e.target.files[0], 'headshot')}
                                                    />
                                                </div>
                                            </div>
                                            <div className="upload-zone-container">
                                                <label className="upload-label">Title Block</label>
                                                <div
                                                    className="upload-dropzone upload-dropzone-small"
                                                    onDrop={(e) => handleDrop(e, 'title')}
                                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                    onClick={() => document.getElementById('title-file-input').click()}
                                                >
                                                    {titlePreview ? (
                                                        <div className="upload-preview">
                                                            <img src={titlePreview} alt="Title preview" style={{ maxWidth: '200px', maxHeight: '75px' }} />
                                                        </div>
                                                    ) : (
                                                        <div className="upload-placeholder">
                                                            <span className="upload-icon">🏷️</span>
                                                            <span>Upload title block</span>
                                                            <span className="upload-hint">White background encouraged <br />Will be scaled to 200×75px</span>
                                                        </div>
                                                    )}
                                                    <input
                                                        id="title-file-input"
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => handleFileSelect(e.target.files[0], 'title')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="branding-actions">
                                    <button
                                        className="branding-save-btn"
                                        onClick={handleSaveBranding}
                                        disabled={savingBranding}
                                    >
                                        {savingBranding ? 'Saving...' : 'Save Branding'}
                                    </button>
                                    {brandingConfig && (
                                        <button
                                            className="branding-remove-btn"
                                            onClick={handleRemoveBranding}
                                        >
                                            Remove Branding
                                        </button>
                                    )}
                                </div>

                                {/* Email Preview */}
                                <div className="email-preview-section">
                                    <h3>Email Preview</h3>
                                    <p className="preview-description">This is how the top of your critical dates email will appear.</p>
                                    <div className="email-preview-container">
                                        <iframe
                                            title="Email Preview"
                                            srcDoc={emailPreviewHtml}
                                            className="email-preview-iframe"
                                            sandbox="allow-same-origin"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* INTEGRATIONS TAB */}
                    {activeTab === 'integrations' && (
                        <div className="settings-card">
                            <div className="settings-card-header">
                                <h2>Integrations</h2>
                            </div>
                            <div className="settings-card-body">
                                <p className="section-description">
                                    Connect your Transaction Management System so KeyFlo can automatically create sale files and sync documents.
                                </p>

                                {tmsMessage && (
                                    <div className={`settings-message ${tmsMessage.type === 'error' ? 'error' : 'success'}`}>
                                        {tmsMessage.text}
                                    </div>
                                )}

                                {/* SkySlope */}
                                <div className="integration-block">
                                    <div className="integration-header">
                                        <div className="integration-title">
                                            <img src="/skyslope_logo.png" alt="SkySlope" className="integration-logo" />
                                            <div>
                                                <strong>SkySlope</strong>
                                                <span className="integration-desc">Transaction management for real estate professionals</span>
                                            </div>
                                        </div>
                                        {tmsConfig?.tms_type === 'skyslope' && (
                                            <span className="tms-connected-badge">● Connected</span>
                                        )}
                                    </div>

                                    {(() => {
                                        const toggleShow = (field) =>
                                            setTmsShowField(s => ({ ...s, [field]: !s[field] }));
                                        const EyeIcon = ({ field }) => (
                                            <button
                                                type="button"
                                                className="tms-eye-btn"
                                                onClick={() => toggleShow(field)}
                                                aria-label={tmsShowField[field] ? 'Hide' : 'Show'}
                                            >
                                                {tmsShowField[field]
                                                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                                }
                                            </button>
                                        );
                                        return tmsConfig?.tms_type === 'skyslope' ? (
                                            <div className="tms-connected-info">
                                                <div className="tms-info-row">
                                                    <span className="tms-info-label">Office</span>
                                                    <span className="tms-info-value">{tmsConfig.office_name || '—'}</span>
                                                </div>
                                                <div className="tms-fields">
                                                    <div className="tms-field-row">
                                                        <div className="input-group">
                                                            <label>Access Key</label>
                                                            <div className="tms-input-wrap">
                                                                <input
                                                                    type={tmsShowField.access_key ? 'text' : 'password'}
                                                                    value={tmsForm.access_key}
                                                                    onChange={e => setTmsForm(f => ({ ...f, access_key: e.target.value }))}
                                                                    autoComplete="off"
                                                                    readOnly
                                                                />
                                                                <EyeIcon field="access_key" />
                                                            </div>
                                                        </div>
                                                        <div className="input-group">
                                                            <label>Access Secret</label>
                                                            <div className="tms-input-wrap">
                                                                <input
                                                                    type={tmsShowField.access_secret ? 'text' : 'password'}
                                                                    value={tmsForm.access_secret || (tmsConfig?.access_secret_configured ? '••••••••' : '')}
                                                                    autoComplete="off"
                                                                    name="skyslope-access-secret"
                                                                    data-1p-ignore
                                                                    data-lpignore="true"
                                                                    data-form-type="other"
                                                                    readOnly
                                                                />
                                                                <EyeIcon field="access_secret" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="tms-action-buttons">
                                                    <button className="tms-disconnect-btn" onClick={handleDisconnectTms}>
                                                        Disconnect
                                                    </button>
                                                    {showImportRetry && (
                                                        <button
                                                            className="tms-connect-btn"
                                                            onClick={handleRetryInboundSync}
                                                            disabled={retryingInboundSync}
                                                        >
                                                            {retryingInboundSync ? 'Starting import...' : 'Retry import'}
                                                        </button>
                                                    )}
                                                </div>
                                                {(showImportStartedMessage || kickoffStillActive || kickoffAwaitingStatus) && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <InboundSyncImportBanner
                                                            visible
                                                            isGuest={userProfile?.is_transaction_guest === true}
                                                        />
                                                    </div>
                                                )}
                                                {showImportRetry && inboundKickoff?.error && (
                                                    <p className="tms-form-hint" style={{ marginTop: '12px', color: '#B45309' }}>
                                                        Import did not start: {inboundKickoff.error}
                                                    </p>
                                                )}
                                                <p className="tms-form-hint" style={{ marginTop: '8px' }}>
                                                    {inboundSyncStatusLabel()}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="tms-form">
                                                <SkyslopeKeyHelp />
                                                <div className="tms-fields">
                                                    <div className="tms-field-row">
                                                        <div className="input-group">
                                                            <label>Access Key</label>
                                                            <input
                                                                type="text"
                                                                value={tmsForm.access_key}
                                                                onChange={e => setTmsForm(f => ({ ...f, access_key: e.target.value }))}
                                                                placeholder="SKYXXXXXXXXXXXXXXXX"
                                                                autoComplete="off"
                                                            />
                                                        </div>
                                                        <div className="input-group">
                                                            <label>Access Secret</label>
                                                            <div className="tms-input-wrap">
                                                                <input
                                                                    type={tmsShowField.access_secret ? 'text' : 'password'}
                                                                    value={tmsForm.access_secret}
                                                                    onChange={e => setTmsForm(f => ({ ...f, access_secret: e.target.value }))}
                                                                    placeholder="Access Secret"
                                                                    autoComplete="off"
                                                                    name="skyslope-access-secret"
                                                                    data-1p-ignore
                                                                    data-lpignore="true"
                                                                    data-form-type="other"
                                                                />
                                                                <EyeIcon field="access_secret" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    className="tms-connect-btn"
                                                    onClick={handleConnectTms}
                                                    disabled={savingTms || !tmsForm.access_key || !tmsForm.access_secret}
                                                >
                                                    {savingTms ? 'Connecting...' : 'Connect SkySlope'}
                                                </button>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Settings;
